/**
 * Signature Request State Machine — the PURE engine behind e-sign requests.
 *
 * Fixes the auditor's #1 and #4 findings:
 *   • The lifecycle is a strict, explicit state machine
 *     ({@link LEGAL_TRANSITIONS}): anything not in the table is rejected
 *     with a reason, and terminal states (signed / declined / voided) have
 *     NO exits — a signed request can never be re-signed, declined, or
 *     voided. Invalid states are unrepresentable, not merely discouraged.
 *   • Consent is NOT a bare boolean anymore: every request records the
 *     exact {@link ConsentDisclosure} text + version the signer was shown,
 *     so "what did they agree to" is answerable years later.
 *   • Signing is guarded by {@link canSign}: request must be outbound
 *     (sent/viewed), consent must be exactly `true`, the typed/drawn
 *     signature value must be non-empty, and the document hash the signer
 *     is looking at must EQUAL the hash the request was issued over —
 *     "what you saw is what you signed" (see `crm/docHash.ts`).
 *
 * Deliberately pure: no store, no firebase, no ambient clocks — ids and
 * timestamps arrive on the inputs. Persistence lives in
 * `src/lib/db/signatureRequests.ts` (the store adapter); this module is the
 * math. It complements (does not replace) `proposalSignature.ts`, which owns
 * the multi-signer hash-chain + certificate; this module owns the REQUEST
 * lifecycle a single outbound signature travels through.
 */

// ---------------------------------------------------------------------------
// Schema
// ---------------------------------------------------------------------------

export type SignatureRequestStatus =
  | 'sent'
  | 'viewed'
  | 'signed'
  | 'declined'
  | 'voided';

/**
 * The exact consent language the signer was shown, plus its version — the
 * ESIGN/UETA disclosure is evidence, so it is recorded verbatim rather than
 * collapsed into a boolean.
 */
export interface ConsentDisclosure {
  text: string;
  version: string;
}

/** The intent artifact — a typed legal name or serialized drawn strokes. */
export interface SignatureData {
  kind: 'typed' | 'drawn';
  value: string;
}

export interface SignatureRequest {
  id: string;
  /** The BusinessDoc this request covers. */
  docId: string;
  /** SHA-256 hex over the canonical doc bytes at issue time. */
  docHash: string;
  signerName: string;
  signerEmail: string;
  status: SignatureRequestStatus;
  /** True only after a successful 'sign' transition. */
  consent: boolean;
  /** The disclosure text/version shown to the signer — never a bare bool. */
  consentDisclosure: ConsentDisclosure;
  /** ISO-8601 UTC. */
  sentAt: string;
  viewedAt?: string;
  signedAt?: string;
  declinedAt?: string;
  declineReason?: string;
  signatureData?: SignatureData;
  /** Recorded verbatim from the caller — never fabricated here. */
  ip?: string;
  userAgent?: string;
}

// ---------------------------------------------------------------------------
// Events — a discriminated union so illegal payloads don't typecheck
// ---------------------------------------------------------------------------

export type SigEvent =
  | { type: 'view'; at?: string; ip?: string; userAgent?: string }
  | {
      type: 'sign';
      at?: string;
      /** Must be exactly true — the recorded consent affirmation. */
      consent: boolean;
      signatureData: SignatureData;
      /** Hash of the document the signer is ACTUALLY looking at. */
      docHash: string;
      ip?: string;
      userAgent?: string;
    }
  | { type: 'decline'; at?: string; reason: string; ip?: string; userAgent?: string }
  | { type: 'void'; at?: string };

/** Which status each event type drives toward. */
const TARGET_STATUS: Record<SigEvent['type'], SignatureRequestStatus> = {
  view: 'viewed',
  sign: 'signed',
  decline: 'declined',
  void: 'voided',
};

/**
 * The full governed transition table. Anything absent is illegal — including
 * every path OUT of signed/declined/voided, so closed requests stay closed.
 * Signing straight from 'sent' is legal (the signer may sign on first open).
 */
export const LEGAL_TRANSITIONS: Record<
  SignatureRequestStatus,
  SignatureRequestStatus[]
> = {
  sent: ['viewed', 'signed', 'declined', 'voided'],
  viewed: ['signed', 'declined', 'voided'],
  signed: [],
  declined: [],
  voided: [],
};

// ---------------------------------------------------------------------------
// Construction
// ---------------------------------------------------------------------------

export interface CreateRequestInput {
  id: string;
  docId: string;
  docHash: string;
  signerName: string;
  signerEmail: string;
  consentDisclosure: ConsentDisclosure;
  /** ISO-8601 UTC issue timestamp (caller-supplied — the engine is pure). */
  sentAt: string;
}

/**
 * Mint a new request in 'sent'. Configuration errors (empty hash, empty
 * disclosure, missing signer identity) throw immediately — a request that
 * cannot prove what was sent to whom must never exist.
 */
export function createRequest(input: CreateRequestInput): SignatureRequest {
  const required: [string, string][] = [
    ['id', input.id],
    ['docId', input.docId],
    ['docHash', input.docHash],
    ['signerName', input.signerName],
    ['signerEmail', input.signerEmail],
    ['sentAt', input.sentAt],
    ['consentDisclosure.text', input.consentDisclosure?.text ?? ''],
    ['consentDisclosure.version', input.consentDisclosure?.version ?? ''],
  ];
  for (const [name, value] of required) {
    if (typeof value !== 'string' || value.trim() === '') {
      throw new Error(`createRequest: '${name}' must be a non-empty string.`);
    }
  }
  return {
    id: input.id,
    docId: input.docId,
    docHash: input.docHash,
    signerName: input.signerName,
    signerEmail: input.signerEmail,
    status: 'sent',
    consent: false,
    consentDisclosure: {
      text: input.consentDisclosure.text,
      version: input.consentDisclosure.version,
    },
    sentAt: input.sentAt,
  };
}

// ---------------------------------------------------------------------------
// Guards
// ---------------------------------------------------------------------------

export interface SignAttempt {
  consent: boolean;
  signatureData?: SignatureData;
  /** Hash of the document rendered in front of the signer right now. */
  docHash: string;
}

/**
 * May this request be signed? Checks, in order:
 *   1. status is 'sent' or 'viewed' (never a closed request);
 *   2. consent is EXACTLY true;
 *   3. the typed/drawn signature value is non-empty;
 *   4. the presented doc hash equals the issued doc hash —
 *      "what you saw is what you signed".
 * When `attempt` is omitted, the request's own fields are evaluated
 * (useful for re-validating a persisted record).
 */
export function canSign(
  req: SignatureRequest,
  attempt?: SignAttempt,
): { ok: boolean; reason?: string } {
  const a: SignAttempt = attempt ?? {
    consent: req.consent,
    signatureData: req.signatureData,
    docHash: req.docHash,
  };
  if (req.status !== 'sent' && req.status !== 'viewed') {
    return {
      ok: false,
      reason: `Cannot sign a request in status '${req.status}' — only 'sent' or 'viewed' requests are signable.`,
    };
  }
  if (a.consent !== true) {
    return {
      ok: false,
      reason:
        'Explicit consent to sign electronically is required (the recorded disclosure must be affirmed).',
    };
  }
  if (!a.signatureData || a.signatureData.value.trim() === '') {
    return {
      ok: false,
      reason: 'Signature value must not be empty — no intent artifact, no signature.',
    };
  }
  if (a.docHash !== req.docHash) {
    return {
      ok: false,
      reason: `Document hash mismatch — the document changed since this request was issued (expected ${req.docHash}, saw ${a.docHash}).`,
    };
  }
  return { ok: true };
}

// ---------------------------------------------------------------------------
// Transitions
// ---------------------------------------------------------------------------

export interface TransitionResult {
  ok: boolean;
  next?: SignatureRequest;
  error?: string;
}

function iso(at?: string): string {
  return at ?? new Date().toISOString();
}

/**
 * Apply one event. Illegal moves return `{ ok: false, error }` and NEVER
 * touch the input (no `next` on the error branch); legal moves return a
 * brand-new record with the status, timestamp, and payload fields stamped.
 * The 'sign' path additionally runs every {@link canSign} guard.
 */
export function applyTransition(
  req: SignatureRequest,
  event: SigEvent,
): TransitionResult {
  const to = TARGET_STATUS[event.type];
  if (!LEGAL_TRANSITIONS[req.status].includes(to)) {
    const allowed = LEGAL_TRANSITIONS[req.status];
    return {
      ok: false,
      error: `Illegal transition: ${req.status} → ${to}. Allowed from '${req.status}': ${
        allowed.length ? allowed.join(', ') : '(terminal state)'
      }.`,
    };
  }

  const at = iso(event.at);
  switch (event.type) {
    case 'view':
      return {
        ok: true,
        next: {
          ...req,
          status: 'viewed',
          viewedAt: req.viewedAt ?? at,
          ip: event.ip ?? req.ip,
          userAgent: event.userAgent ?? req.userAgent,
        },
      };
    case 'sign': {
      const guard = canSign(req, {
        consent: event.consent,
        signatureData: event.signatureData,
        docHash: event.docHash,
      });
      if (!guard.ok) return { ok: false, error: guard.reason };
      return {
        ok: true,
        next: {
          ...req,
          status: 'signed',
          consent: true,
          signedAt: at,
          signatureData: { ...event.signatureData },
          ip: event.ip ?? req.ip,
          userAgent: event.userAgent ?? req.userAgent,
        },
      };
    }
    case 'decline': {
      if (typeof event.reason !== 'string' || event.reason.trim() === '') {
        return {
          ok: false,
          error: 'Decline requires a non-empty reason — silence is not evidence.',
        };
      }
      return {
        ok: true,
        next: {
          ...req,
          status: 'declined',
          declinedAt: at,
          declineReason: event.reason,
          ip: event.ip ?? req.ip,
          userAgent: event.userAgent ?? req.userAgent,
        },
      };
    }
    case 'void':
      return { ok: true, next: { ...req, status: 'voided' } };
  }
}
